/**
 * 素材類型
 */
export enum AssetType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  YOUTUBE = 'YOUTUBE',
  INSTAGRAM = 'INSTAGRAM',
}

/**
 * 素材處理狀態
 */
export enum ProcessingStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/**
 * 儲存層級（GCP 全量 vs NAS 索引模式）
 */
export enum StorageTier {
  GCP_FULL = 'GCP_FULL',       // 全部上傳 GCP（手動上傳的現有行為）
  NAS_INDEXED = 'NAS_INDEXED', // 只有縮圖在 GCP，原始檔在 NAS
}

/**
 * NAS 同步狀態
 */
export type NasSyncStatus = 'running' | 'completed' | 'failed' | 'idle';

/**
 * NAS 同步統計
 */
export interface NasSyncStats {
  scanned: number;
  added: number;
  updated: number;
  deleted: number;  // 軟刪除（original_available = false）
  skipped: number;
  errors: number;
}

/**
 * NAS 同步記錄
 */
export interface NasSyncRun {
  id: string;
  triggeredBy: 'cron' | 'manual' | 'scheduler';
  rootPaths: string[];
  startedAt: string;
  completedAt?: string;
  status: NasSyncStatus;
  stats: NasSyncStats;
  errorMessage?: string;
}

/**
 * 素材解析度配置
 */
export interface ResolutionConfig {
  width: number;
  height: number;
  quality: number;
}

/**
 * 素材元數據
 */
export interface AssetMetadata {
  id: string;
  originalFilename: string;
  description?: string;
  sourceType: AssetType;
  uploadDate: string;
  modifiedDate: string;
  capturedDate?: string;
  uploader: string;
  uploaderEmail?: string;
  activityId: string;
  resolution?: string;
  fileSize?: number;
  mimeType?: string;

  // URLs for different resolutions
  thumbnailUrl: string; // 240px
  hdUrl: string; // 1080px
  originalUrl: string; // Original resolution

  // Storage paths
  sourcePath?: string; // 本地原始路徑 (相對於上傳目錄的路徑，用於識別原始位置，可編輯)
  originalPath?: string; // 已廢棄，請使用 sourcePath

  // Tags and categorization
  tags: string[];
  tagKeys?: string[];
  activityTags: string[];
  assetTypeTags: string[];

  // AI generated metadata
  aiDescription?: string;
  aiTags?: string[];
  visionLabels?: string[];

  // External sources
  externalUrl?: string;

  // Processing status
  processingStatus: ProcessingStatus;

  // Folder path
  folderPath?: string;

  // NAS integration fields (optional — only set for NAS_INDEXED assets)
  storageTier?: StorageTier;
  nasPath?: string;           // e.g. NAS:///volume1/Events/2024/photo.jpg
  nasChecksum?: string;       // MD5 for dedup
  nasQuickSig?: string;       // "{size}:{mtime}" fast comparison
  originalAvailable?: boolean;
  lastNasSync?: string;
  isStarred?: boolean;
}

export interface BlockedNasPath {
  path: string;
  createdAt: string;
}

/**
 * 過濾選項
 */
export interface FilterOptions {
  search?: string;
  starredOnly?: boolean;
  sourceType?: AssetType | 'ALL';
  activityId?: string;
  tag?: string;
  folder?: string;
  dateFrom?: string;
  dateTo?: string;
  uploader?: string;
  processingStatus?: ProcessingStatus;
  /** Explorer mode: filter assets whose nas_path starts with this prefix (server-side) */
  nasPathPrefix?: string;
  /** When true, only match assets whose nas_path exactly equals nasPathPrefix (no subtree) */
  nasPathExact?: boolean;
}

/** NAS directory tree node returned by the explorer API */
export interface ExplorerDirNode {
  path: string;
  name: string;
  parentPath?: string | null;
  depth: number;
  directAssetCount: number;
  totalAssetCount: number;
  hasChildren: boolean;
}

/**
 * 分頁參數
 */
export interface PaginationParams {
  page: number;
  pageSize: number;
}

/**
 * 分頁結果
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * 上傳請求
 */
export interface UploadRequest {
  file: any; // File (browser) | Buffer (Node.js)
  filename: string;
  activityId: string;
  sourceType: AssetType;
  tags?: string[];
  folderPath?: string;
  capturedDate?: string;
  externalUrl?: string;
  originalPath?: string; // 上傳者提供的原始路徑
}

/**
 * 更新請求
 */
export interface UpdateAssetRequest {
  description?: string;
  tags?: string[];
  tagKeys?: string[];
  activityTags?: string[];
  assetTypeTags?: string[];
  folderPath?: string;
  capturedDate?: string;
  sourcePath?: string; // 本地原始路徑（可編輯）
  isStarred?: boolean;
}

export interface TagDefinition {
  tagKey: string;
  category: string;
  name: string;
  usageCount?: number;
  createdAt?: string;
}

/**
 * Vision API 分析結果
 */
export interface VisionAnalysisResult {
  labels: Array<{
    description: string;
    score: number;
  }>;
  objects?: Array<{
    name: string;
    score: number;
  }>;
  text?: string;
  colors?: Array<{
    color: {
      red: number;
      green: number;
      blue: number;
    };
    score: number;
    pixelFraction: number;
  }>;
}

/**
 * 活動資訊
 */
export interface Activity {
  id: string;
  name: string;
  description?: string;
  createdDate: string;
  tags?: string[];
}

/**
 * 資料夾資訊
 */
export interface FolderInfo {
  path: string;
  name: string;
  assetCount: number;
  children?: FolderInfo[];
}

/**
 * API 響應包裝
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * MCP 工具參數
 */
export interface MCPSearchParams {
  query?: string;
  activityId?: string;
  tags?: string[];
  limit?: number;
}

export interface MCPAssetResult {
  id: string;
  filename: string;
  description?: string;
  url: string;
  thumbnailUrl: string;
  tags: string[];
  uploadDate: string;
}
